import { useState } from "react"
import type { ChangeEvent, FormEvent } from "react"
import { Link } from "react-router-dom"
import EntryFormField, {
  FIELD_CONTROL_CLASS,
} from "@/components/entry/EntryFormField"
import EmptyMessage from "@/components/common/EmptyMessage"
import Loading from "@/components/common/Loading"
import StatusBadge from "@/components/common/StatusBadge"
import { useEntryLookup } from "@/domain/entry/hooks/EntryLookupHook"
import { EntryStatusPayload } from "@bracket/idl/main/entry/model/EntryPt"

export default function EntryLookupPage() {
  const {
    useEntryLookupState,
    setGymContactPhoneNo,
    setPassword,
    search,
    openDetail,
    closeDetail,
    cancelEntry,
  } = useEntryLookup()
  const {
    gymContactPhoneNo,
    password,
    summaries,
    detail,
    isSearched,
    isLoading,
    errorMessage,
  } = useEntryLookupState()

  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState("")

  const isCanceled = detail?.status === EntryStatusPayload.ENTRY_CANCELED

  function onChangeGymContactPhoneNo(event: ChangeEvent<HTMLInputElement>) {
    setGymContactPhoneNo(event.target.value)
  }

  function onChangePassword(event: ChangeEvent<HTMLInputElement>) {
    setPassword(event.target.value)
  }

  function onSubmitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setIsCancelOpen(false)
    void search()
  }

  function onClickCloseDetail() {
    setIsCancelOpen(false)
    closeDetail()
  }

  function onClickOpenCancel() {
    setIsCancelOpen(true)
  }

  function onClickCloseCancel() {
    setIsCancelOpen(false)
    setCancelReason("")
  }

  function onChangeCancelReason(event: ChangeEvent<HTMLTextAreaElement>) {
    setCancelReason(event.target.value)
  }

  function onSubmitCancel(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!detail) return

    void cancelEntry(detail.entryNo, cancelReason).then(() => {
      setIsCancelOpen(false)
      setCancelReason("")
    })
  }

  return (
    <section className="app-page max-w-[672px]">
      <div className="app-page-head">
        <p className="app-txt-eyebrow text-app-primary-600">
          APPLICATION LOOKUP
        </p>
        <h1 className="app-txt-h1 mt-3">참가 신청 조회</h1>
        <p className="app-txt-body2 mt-3 leading-6 text-app-text-300">
          로그인 없이 체육관 연락처와 신청 시 정한 비밀번호로 신청 내역을 조회할
          수 있습니다. 비밀번호를 잊으셨다면 대회 운영측에 문의해 주세요.
        </p>
      </div>

      <form
        onSubmit={onSubmitSearch}
        noValidate
        className="app-card"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <EntryFormField
            controlId="lookup-gym-contact-phone-no"
            label="체육관 연락처"
            isRequired
          >
            <input
              id="lookup-gym-contact-phone-no"
              type="tel"
              inputMode="tel"
              value={gymContactPhoneNo}
              maxLength={13}
              onChange={onChangeGymContactPhoneNo}
              placeholder="010-1234-5678"
              className={FIELD_CONTROL_CLASS}
            />
          </EntryFormField>

          <EntryFormField
            controlId="lookup-password"
            label="신청 비밀번호"
            isRequired
          >
            <input
              id="lookup-password"
              type="password"
              autoComplete="current-password"
              value={password}
              maxLength={30}
              onChange={onChangePassword}
              placeholder="신청 시 정한 비밀번호"
              className={FIELD_CONTROL_CLASS}
            />
          </EntryFormField>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="focus-ring app-btn app-btn-primary app-btn-block mt-6"
        >
          조회
        </button>
      </form>

      {isLoading && <Loading label="신청 내역을 불러오는 중" />}

      {!isLoading && isSearched && errorMessage && (
        <p
          role="alert"
          className="app-alert app-alert-error mt-5"
        >
          {errorMessage}
        </p>
      )}

      {!isLoading && isSearched && !errorMessage && summaries.length === 0 && (
        <EmptyMessage message="조회된 신청 내역이 없습니다." />
      )}

      {!isLoading && !detail && summaries.length > 0 && (
        <ul className="mt-5 flex list-none flex-col gap-4">
          {summaries.map((summary) => (
            <li
              key={summary.entryNo}
              className="app-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="app-txt-caption font-mono text-app-text-200">
                    {summary.entryNo}
                  </p>
                  <h2 className="app-txt-h3 mt-1.5">
                    {summary.tournamentName}
                  </h2>
                  <p className="app-txt-body2 mt-1.5 text-app-text-200">
                    {summary.tournamentStartText} · 선수 {summary.playerCount}명
                    · {summary.sportLabels}
                  </p>
                  <p className="app-txt-caption mt-1 text-app-text-200">
                    접수 {summary.appliedAtText}
                  </p>
                </div>
                <StatusBadge
                  label={summary.statusLabel}
                  className="shrink-0"
                />
              </div>

              <button
                type="button"
                onClick={() => openDetail(summary.entryNo)}
                className="focus-ring app-btn app-btn-outline app-btn-block mt-5"
              >
                신청 상세 보기
              </button>
            </li>
          ))}
        </ul>
      )}

      {!isLoading && detail && (
        <section className="app-card mt-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="app-txt-caption font-mono text-app-text-200">
                {detail.entryNo}
              </p>
              <h2 className="app-txt-h2 mt-1.5">{detail.gymName}</h2>
              <p className="app-txt-body2 mt-1.5 text-app-text-200">
                담당자 {detail.gymContactName}
              </p>
              {detail.tournamentName && (
                <p className="app-txt-body2 mt-1 text-app-text-200">
                  {detail.tournamentName} · {detail.tournamentPeriodText}
                </p>
              )}
            </div>
            {/* 아래 표의 선수 상태와 헷갈리지 않게 무엇의 상태인지 붙여 둔다 */}
            <span className="flex shrink-0 flex-col items-end gap-1">
              <span className="app-txt-caption text-app-text-200">
                신청 상태
              </span>
              <StatusBadge label={detail.statusLabel} />
            </span>
          </div>

          {isCanceled && detail.canceledReason && (
            <p className="app-alert app-alert-error mt-5">
              취소 사유 — {detail.canceledReason}
            </p>
          )}

          <div className="app-surface mt-6 overflow-x-auto">
            <table className="app-table">
              <caption className="sr-only">신청 선수 목록</caption>
              <thead>
                <tr>
                  <th scope="col">선수</th>
                  <th scope="col">종목 · 체중</th>
                  <th
                    scope="col"
                    className="text-right"
                  >
                    선수 상태
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.players.map((player) => (
                  <tr key={player.id}>
                    <td>
                      <span className="font-bold">{player.name}</span>
                      <span className="app-txt-caption mt-0.5 block font-medium text-app-text-200">
                        {player.birthDate} · {player.genderLabel} ·{" "}
                        {player.divisionLabel}
                      </span>
                    </td>
                    <td className="text-app-text-300">
                      {player.weightText
                        ? `${player.sportLabel} · ${player.weightText}`
                        : player.sportLabel}
                      <span className="app-txt-caption mt-0.5 block font-medium text-app-text-200">
                        {player.weightClassName}
                      </span>
                    </td>
                    <td className="text-right text-app-text-200">
                      {player.statusLabel}
                      {player.rejectedReason && (
                        <span className="app-txt-caption mt-0.5 block text-app-red-300">
                          {player.rejectedReason}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isCanceled && (
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <Link
                to={`/application/modify?entryNo=${detail.entryNo}`}
                className="focus-ring app-btn app-btn-outline"
              >
                신청 수정
              </Link>
              <button
                type="button"
                onClick={onClickOpenCancel}
                className="focus-ring app-btn app-btn-danger-outline"
              >
                신청 취소
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={onClickCloseDetail}
            className="focus-ring app-btn app-btn-quiet app-btn-block mt-3"
          >
            ← 목록으로
          </button>

          {isCancelOpen && (
            <form
              onSubmit={onSubmitCancel}
              className="mt-5 border border-app-red-200 bg-app-red-100 p-4"
            >
              <label
                htmlFor="cancel-reason"
                className="app-label text-app-red-300"
              >
                취소 사유
              </label>
              <textarea
                id="cancel-reason"
                value={cancelReason}
                rows={3}
                maxLength={200}
                required
                onChange={onChangeCancelReason}
                className="focus-ring app-field mt-2"
              />
              <div className="mt-3 flex gap-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="focus-ring app-btn app-btn-danger"
                >
                  취소 확정
                </button>
                <button
                  type="button"
                  onClick={onClickCloseCancel}
                  className="focus-ring app-btn app-btn-outline"
                >
                  닫기
                </button>
              </div>
            </form>
          )}
        </section>
      )}
    </section>
  )
}
